import { usePageTitle } from "@/hooks/usePageTitle";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const S = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="font-heading text-2xl text-navy">{title}</h2>
    <div className="mt-3 space-y-3 text-[15px] text-[#57534E] leading-relaxed">{children}</div>
  </section>
);

export default function PrivacyPolicy() {
  usePageTitle("Privacy & Cookies Policy");
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 lg:px-8 pt-28 pb-20" data-testid="privacy-page">
        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-teal">Your data, respected</span>
        <h1 className="mt-3 font-heading text-4xl sm:text-5xl tracking-tight text-navy">Privacy & Cookies Policy</h1>
        <p className="mt-4 text-sm text-[#78716C]">Last updated: August 2026 · My Best Body Club, United Kingdom</p>

        <S title="Who we are">
          <p>
            My Best Body Club ("MBBC", "we") is a UK nutrition and body-composition membership club founded by
            Andrea Wood, Nutritional Therapist DipION Fd/Sc. For anything relating to your personal data,
            contact us at <a href="mailto:andrea@mybestbodyclub.com" className="text-teal font-medium">andrea@mybestbodyclub.com</a>.
          </p>
        </S>

        <S title="What we collect and why">
          <ul className="list-disc pl-5 space-y-2">
            <li><strong className="text-navy">Enquiries & waitlist:</strong> your name and email when you use the "Learn More" or waitlist forms — so we can send you the programme details you asked for.</li>
            <li><strong className="text-navy">Member accounts:</strong> your name, email and a securely hashed password — so you can log in to the member area.</li>
            <li><strong className="text-navy">Progress tracking:</strong> body-composition readings you choose to enter in your personal tracker — visible only to you, so you can monitor your own progress.</li>
            <li><strong className="text-navy">Payments:</strong> handled entirely by Stripe. We never see or store your card details — we only receive confirmation that a payment succeeded.</li>
            <li><strong className="text-navy">Messages & feedback:</strong> anything you send us through the contact or feedback forms, so we can reply and improve the club.</li>
          </ul>
        </S>

        <S title="Emails — no spam, ever">
          <p>
            We send two kinds of email: <strong className="text-navy">service emails</strong> (password resets,
            welcome emails, payment confirmations) and, only if you've opted in, <strong className="text-navy">occasional
            updates</strong> such as recipes and launch news. Every marketing email includes an unsubscribe link, and we
            never sell or share your details with third parties for their own marketing.
          </p>
        </S>

        <S title="Who processes your data for us">
          <ul className="list-disc pl-5 space-y-2">
            <li><strong className="text-navy">Stripe</strong> — payment processing</li>
            <li><strong className="text-navy">Resend</strong> — sending service emails</li>
            <li><strong className="text-navy">MailerLite</strong> — managing newsletter and waitlist emails you've opted in to</li>
            <li><strong className="text-navy">Amazon Web Services</strong> — secure storage of member content and our database hosting</li>
          </ul>
          <p>Each provider only receives the minimum data needed to do its job.</p>
        </S>

        <S title="Cookies">
          <p>
            We only use <strong className="text-navy">strictly necessary cookies</strong> — a single secure session
            cookie that keeps you logged in to the member area. We do not use advertising, analytics or tracking
            cookies, which is why you won't see a cookie consent pop-up on this site. If that ever changes, we'll
            ask for your consent first.
          </p>
        </S>

        <S title="How long we keep your data">
          <p>
            Account data is kept while your membership is active. Enquiry and waitlist details are kept until our
            launch communications are complete or you unsubscribe. You can ask us to delete your data at any time.
          </p>
        </S>

        <S title="Your rights">
          <p>
            Under UK GDPR you can ask us to access, correct, export or delete the personal data we hold about you,
            and you can withdraw marketing consent at any time. Just email{" "}
            <a href="mailto:andrea@mybestbodyclub.com" className="text-teal font-medium">andrea@mybestbodyclub.com</a>{" "}
            and we'll act on it promptly. You also have the right to complain to the ICO (ico.org.uk) if you're
            unhappy with how we've handled your data.
          </p>
        </S>
      </div>
      <Footer />
    </div>
  );
}
